/**
 * What to draw in a competition's badge (specs/competitions-hub-client AC-15.2/15.3).
 *
 * Pure. Three surfaces render this badge — the hub card, the hub hero and the
 * competition shell header — and before this helper each carried its own
 * `logoUrl ? Image : monogram` branch.
 *
 * The flag branch exists because `logoUrl` can legitimately be null for a competition
 * that very much has an identity: the server suppresses `league.logo` when it is a
 * rights-holder's mark we cannot ship (the UEFA Champions League logo). Rather than
 * teach the client which competitions those are, the flag code comes from the existing
 * `resolveLeagueCountryCodeForFlag`, which already maps every UEFA competition name to
 * `eu` for the fixtures and create-challenge surfaces. No competition vocabulary is
 * hardcoded here — a new European cup resolves for free.
 *
 * That name inference is now a fallback: `brand.flagCode` states the flag outright, because
 * a competition whose display name is de-branded ("Champions 26/27") matches no rule.
 */

import { resolveLeagueCountryCodeForFlag } from '../../shared/helpers/leagueCountryToFlagCode.helper';

/** How many characters of the short name / name the monogram keeps. */
const MONOGRAM_LENGTH = 3;

export type CompetitionCrest =
  | { kind: 'logo'; logoUrl: string }
  | { kind: 'flag'; countryCode: string }
  | { kind: 'monogram'; monogram: string };

export interface CompetitionCrestInput {
  /** Server-resolved crest. Null means "compose one" — never "this competition has no identity". */
  logoUrl?: string | null;
  /** The name the user sees (brand name when there is one). Drives both flag and monogram. */
  name: string;
  shortName?: string | null;
  /**
   * `brand.flagCode` — the flag to compose, stated rather than inferred (AC-15.9). Wins over
   * the name rules below, which only ever were a guess.
   */
  flagCode?: string | null;
}

/**
 * The badge's last resort: the letters shown when there is no crest and no flag, and the
 * fallback when a flag image fails to load (AC-15.6). A flag that 404s must not leave an
 * empty circle where the competition's identity should be.
 */
export function competitionMonogram({ name, shortName }: CompetitionCrestInput): string {
  const source = shortName != null && shortName.trim() !== '' ? shortName : name;
  return source.trim().slice(0, MONOGRAM_LENGTH).toUpperCase();
}

export function resolveCompetitionCrest(input: CompetitionCrestInput): CompetitionCrest {
  const logoUrl = input.logoUrl?.trim();
  if (logoUrl != null && logoUrl !== '') return { kind: 'logo', logoUrl };

  // Stated beats inferred: a de-branded display name ("Champions 26/27") matches no league
  // rule, and the competition's identity must not depend on the wording of its title.
  const stated = input.flagCode?.trim();
  if (stated != null && stated !== '') return { kind: 'flag', countryCode: stated };

  const countryCode = resolveLeagueCountryCodeForFlag(null, input.name);
  if (countryCode != null) return { kind: 'flag', countryCode };

  return { kind: 'monogram', monogram: competitionMonogram(input) };
}
